import { type ComponentProps, RefObject, useCallback, useEffect, useState } from 'react';

import { useShopProvider } from '#provider/useShopProvider.js';
import {
  type ConsumerCheckoutCartsResponse,
  useShowCart,
  useShowConsumerCheckout,
  useShowConsumerCheckoutRequiredFields,
} from '@givebacks/api/store';
import { FormProvider } from '@givebacks/formy/components';
import {
  TokenExCardTranslations,
  useBreakPoints,
  useFormListValidator,
  usePlatformService,
} from 'core';
import { ICredential, TokenExCardTypes, useShowCause, useUserData } from 'core/Api';
import { CheckoutCardProvider, CheckoutDonationLegal } from 'core/Components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Affix,
  Box,
  CloseButton,
  CollapsibleCard,
  DonorInfoForm,
  LoadingOverlay,
  Modal,
  Stack,
  rem,
} from 'ui';

import { useShopRoutes } from '../../routes/useShopRoutes';
import { ApplyRewards } from './ApplyRewards/ApplyRewards';
import classes from './Checkout.module.css';
import { NativeCheckoutModal } from './Checkout.native';
import { CheckoutButton } from './CheckoutButton';
import { CheckoutConfirmationModal } from './CheckoutConfirmationModal';
import { CheckoutFees } from './CheckoutFees/CheckoutFees';
import { CheckoutKeepMeUpdated } from './CheckoutKeepMeUpdated/CheckoutKeepMeUpdated';
import { CheckoutReCAPTCHA } from './CheckoutReCAPTCHA/CheckoutReCAPTCHA';
import { CheckoutShopToGiveEnrollmentSection } from './CheckoutShopToGiveEnrollmentSection/CheckoutShopToGiveEnrollmentSection';
import { NPOCheckoutConfirmationModal } from './NPOCheckoutConfirmationModal';
import { OrderSummary } from './OrderSummary';
import { CheckoutAdditionalFields } from './components/CheckoutAdditionalFields/CheckoutAdditionalFields';
import { CheckoutAddressForm } from './components/CheckoutAddressForm/CheckoutAddressForm';
import { CheckoutAnonymousDonationForm } from './components/CheckoutAnonymousDonationForm/CheckoutAnonymousDonationForm';
import { CheckoutGivingaLegal } from './components/CheckoutGivingaLegal/CheckoutGivingaLegal';
import { DiscountSection } from './components/DiscountSection/DiscountSection';
import type { OrderCheckoutProps } from './order-checkout-types';
import { getCartIdsFromCheckout, getCartItemCountFromCheckout, getStoreItem } from './utils';

type CheckoutCardSelectionProps = {
  onCredentialChange: (credential?: TokenExCardTranslations) => void;
};

const CheckoutCardSelection = ({ onCredentialChange }: CheckoutCardSelectionProps) => {
  const handleCredentialChange = (credential?: ICredential | TokenExCardTypes) => {
    if (!credential) return onCredentialChange(undefined);

    const translationKey = typeof credential === 'string' ? credential : credential.card_type;
    const credentialType = TokenExCardTranslations[translationKey];
    onCredentialChange(credentialType);
  };

  return <CheckoutCardProvider onCredentialChange={handleCredentialChange} />;
};

const CheckoutCardSelectionGuard = ({
  checkoutId,
  onCredentialChange,
}: CheckoutCardSelectionProps & { checkoutId: string }) => {
  const { data: isPaymentMethodNeeded } = useShowConsumerCheckout({
    checkoutId,
    select: useCallback(
      (data: ConsumerCheckoutCartsResponse) => data.checkout.payment_method_needed,
      []
    ),
  });

  if (!isPaymentMethodNeeded) return null;
  return <CheckoutCardSelection onCredentialChange={onCredentialChange} />;
};

const OrderCheckoutSection = ({ checkoutId, onSuccess }: OrderCheckoutProps) => {
  const [pendingCredential, setPendingCredential] = useState<TokenExCardTranslations | undefined>(
    undefined
  );

  const { data: requiredFields } = useShowConsumerCheckoutRequiredFields({
    checkoutId,
  });

  const { data: user } = useUserData();

  const { data: storeItem } = useShowCart({
    cartId: checkoutId,
    queryKeyId: 'user',
    select: getStoreItem,
  });
  const { data: cause } = useShowCause(storeItem?.cause_id);

  const { isSmallScreen } = useBreakPoints();

  if (!checkoutId) return null;
  return (
    <CollapsibleCard headerTitle="Order Checkout" isCardExpanded={true}>
      <Stack gap={isSmallScreen ? 'md' : 'lg'}>
        <ApplyRewards checkoutId={checkoutId} />
        <CheckoutCardSelectionGuard
          checkoutId={checkoutId}
          onCredentialChange={setPendingCredential}
        />
        <DonorInfoForm
          id="donor_info"
          title="Your Information"
          required
          isPhoneNumberRequired={Boolean(requiredFields?.phone_number)}
          useFormListValidator={useFormListValidator}
          initialValues={{
            first_name: user?.first_name,
            last_name: user?.last_name,
            email: user?.email,
          }}
        />
        <CheckoutAnonymousDonationForm checkoutId={checkoutId} />
        <CheckoutAddressForm checkoutId={checkoutId} />
        <CheckoutAdditionalFields checkoutId={checkoutId} />

        <DiscountSection checkoutId={checkoutId} />

        <CheckoutFees checkoutId={checkoutId} pendingCredential={pendingCredential} />

        <CheckoutGivingaLegal checkoutId={checkoutId} />

        <CheckoutDonationLegal />
        <CheckoutKeepMeUpdated causeName={cause?.name} />
        <CheckoutReCAPTCHA />
        <CheckoutButton checkoutId={checkoutId} onSuccess={onSuccess} />
      </Stack>
    </CollapsibleCard>
  );
};

const CheckoutContent = ({
  onClose,
  checkoutId,
  onSuccess,
  containerProps,
}: {
  onClose: () => void;
  checkoutId: string;
  onSuccess?: () => void;
  containerProps?: React.ComponentProps<typeof Stack>;
}) => {
  const { data: cartIds } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartIdsFromCheckout,
  });

  const {
    data: cartItemCount,
    isLoading,
    status,
    isLoadingError,
  } = useShowConsumerCheckout({
    checkoutId: checkoutId,
    select: getCartItemCountFromCheckout,
  });

  useEffect(() => {
    if (status !== 'pending') {
      if (isLoadingError) {
        onClose();
      }
      if (cartItemCount === 0) {
        onClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItemCount, isLoadingError, status]);

  if (isLoading) {
    return <LoadingOverlay visible={true} />;
  }

  // Redundant check for checkoutId, but it's here for ts compliance.
  if (cartItemCount === 0 || !cartIds) {
    return null;
  }

  return (
    <Stack className={classes.CheckoutContent_stack} {...containerProps}>
      <OrderSummary checkoutId={checkoutId} />

      <CheckoutShopToGiveEnrollmentSection checkoutId={checkoutId} />

      <OrderCheckoutSection checkoutId={checkoutId} onSuccess={onSuccess ?? onClose} />
    </Stack>
  );
};

/** Guards against checkoutId being undefined for the checkout content to render correctly
 * if checkoutId is undefined, the checkout content will not render and the onClose function will be called
 */
const CheckoutContentGuard = ({
  checkoutId,
  onClose,
  onSuccess,
  containerProps,
}: Omit<ComponentProps<typeof CheckoutContent>, 'checkoutId'> & { checkoutId?: string }) => {
  useEffect(() => {
    if (!checkoutId) {
      onClose();
    }
  }, [checkoutId, onClose]);

  if (!checkoutId) return null;
  return (
    <CheckoutContent
      checkoutId={checkoutId}
      onClose={onClose}
      onSuccess={onSuccess}
      containerProps={containerProps}
    />
  );
};

const CheckoutPage = () => {
  const { checkoutId } = useParams();

  if (!checkoutId) return null;

  return (
    <Stack gap="xl">
      <CheckoutContent checkoutId={checkoutId} onClose={() => null} onSuccess={() => null} />
    </Stack>
  );
};

const CheckoutModalCloseButton = ({ onClick }: { onClick?: () => void }) => {
  return (
    <Affix position={{ top: 20, right: 20 }}>
      <CloseButton onClick={onClick} c="white" variant="transparent" />
    </Affix>
  );
};

/**
 * CheckoutModal is a modal that displays the checkout page.
 * It is used in the web version of the app.
 */

const CheckoutModal = () => {
  const platform = usePlatformService();
  const { checkoutId } = useParams();
  const { closeCheckoutRoute } = useShopRoutes();
  const { isMediumScreen } = useBreakPoints();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const { isNPOStore } = useShopProvider();
  const navigate = useNavigate();

  if (platform.isNative) {
    return <NativeCheckoutModal />;
  }

  return (
    <FormProvider>
      {({ formScrollableRef }) => (
        <Stack>
          <Modal.Root
            opened={!isConfirmationModalOpen}
            onClose={closeCheckoutRoute}
            closeOnClickOutside={false}
            closeOnEscape={false}
            styles={
              isMediumScreen
                ? {
                    inner: {
                      paddingTop: rem(80),
                      paddingBottom: 0,
                    },
                  }
                : {}
            }
          >
            {/*
           The removal of padding should be a negative instead of a 0,
           this would account the size of the modal
         */}
            <Modal.Overlay />
            <CheckoutModalCloseButton onClick={closeCheckoutRoute} />
            <Modal.Content
              /** The default transition on the modal causes a transform(XXX) effect on the modal- this will interfere with the absolutely portioned iframe elements in the modal */
              transitionProps={{ transition: 'fade', duration: 250 }}
              p={0}
              radius="md"
              bg="transparent"
              style={{
                boxShadow: 'none',
                overflow: 'scroll',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
              }}
              ref={formScrollableRef as unknown as RefObject<HTMLDivElement>}
            >
              <Modal.Body p={0} maw="750">
                <Box maw="750" p={0}>
                  <CheckoutContentGuard
                    onClose={closeCheckoutRoute}
                    checkoutId={checkoutId}
                    onSuccess={() => {
                      if (isNPOStore) {
                        // Route is handled in memberhub.js/src/App.tsx
                        navigate(`/shop/post-checkout?checkout_id=${checkoutId}`);
                      } else {
                        setIsConfirmationModalOpen(true);
                      }
                    }}
                  />
                </Box>
              </Modal.Body>
            </Modal.Content>
          </Modal.Root>

          {isNPOStore ? (
            <NPOCheckoutConfirmationModal
              opened={isConfirmationModalOpen}
              onClose={closeCheckoutRoute}
              cartId={checkoutId}
            />
          ) : (
            <CheckoutConfirmationModal
              opened={isConfirmationModalOpen}
              onClose={closeCheckoutRoute}
              checkoutId={checkoutId}
            />
          )}
        </Stack>
      )}
    </FormProvider>
  );
};

export { CheckoutContent, CheckoutModal, CheckoutPage };
